import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { Loader2, Brain, Wrench, Sparkles, AlertCircle, Check } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type {
  StreamingPhase,
  StreamingToolSummary,
  ThreadsStreamingState,
} from '@/hooks/useThreadsStreaming';

export interface StreamingMessageProps {
  /** Current streaming state */
  state: ThreadsStreamingState;
  /** Class name for the container */
  className?: string;
}

/**
 * Map tool names to friendly display names
 */
const TOOL_DISPLAY_NAMES: Record<string, string> = {
  CRM_Tool: 'CRM',
  Pinecone_RAG: 'WorldVia Knowledge Base',
  Web_Search: 'Web Search',
  Web_Content: 'Web Scrape',
  Image_Search: 'Photo Search',
  Writer: 'Writing',
  Longform_Writer: 'Writing', // legacy alias
  // ProposeAction is disabled
  // ProposeAction: 'Action',
};

function getToolDisplayName(toolName: string): string {
  return TOOL_DISPLAY_NAMES[toolName] ?? toolName.replace(/_/g, ' ');
}

/**
 * Phase indicator component
 */
interface PhaseIndicatorProps {
  phase: StreamingPhase;
  tools: StreamingToolSummary[];
}

const PhaseIndicator: React.FC<PhaseIndicatorProps> = ({ phase, tools }) => {
  const phaseConfig = useMemo(() => {
    switch (phase) {
      case 'planning':
        return {
          icon: Brain,
          text: 'Planning response...',
          color: 'text-blue-500',
        };
      case 'tools':
        return {
          icon: Wrench,
          text: 'Running tools...',
          color: 'text-amber-500',
        };
      case 'synthesizing':
        return {
          icon: Sparkles,
          text: 'Generating response...',
          color: 'text-orange-500',
        };
      case 'complete':
        return {
          icon: Check,
          text: 'Complete',
          color: 'text-green-500',
        };
      case 'error':
        return {
          icon: AlertCircle,
          text: 'Error occurred',
          color: 'text-red-500',
        };
      default:
        return {
          icon: Loader2,
          text: 'Thinking...',
          color: 'text-orange-500',
        };
    }
  }, [phase]);

  const Icon = phaseConfig.icon;
  const isAnimated = phase !== 'complete' && phase !== 'error';

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Icon
          className={`h-4 w-4 ${phaseConfig.color} ${isAnimated ? 'animate-pulse' : ''}`}
          aria-hidden
        />
        <span className={`text-sm font-medium ${phaseConfig.color}`}>
          {phaseConfig.text}
        </span>
      </div>

      {/* Tool badges during tools phase */}
      {phase === 'tools' && tools.length > 0 && (
        <div className="flex flex-wrap gap-1.5 ml-6">
          {tools.map((tool) => (
            <ToolBadge key={tool.toolName} tool={tool} />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Individual tool badge
 */
interface ToolBadgeProps {
  tool: StreamingToolSummary;
}

const ToolBadge: React.FC<ToolBadgeProps> = ({ tool }) => {
  const statusConfig = useMemo(() => {
    switch (tool.status) {
      case 'running':
      case 'partial':
        return {
          variant: 'secondary' as const,
          className: 'border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700',
          showSpinner: true,
        };
      case 'success':
        return {
          variant: 'secondary' as const,
          className: 'border-green-300 bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700',
          showSpinner: false,
        };
      case 'failed':
        return {
          variant: 'destructive' as const,
          className: '',
          showSpinner: false,
        };
      default:
        return {
          variant: 'outline' as const,
          className: '',
          showSpinner: false,
        };
    }
  }, [tool.status]);

  return (
    <Badge
      variant={statusConfig.variant}
      className={`text-xs font-normal ${statusConfig.className}`}
    >
      {statusConfig.showSpinner && (
        <Loader2 className="h-3 w-3 mr-1 animate-spin" aria-hidden />
      )}
      {tool.status === 'success' && (
        <Check className="h-3 w-3 mr-1" aria-hidden />
      )}
      {getToolDisplayName(tool.toolName)}
    </Badge>
  );
};

/**
 * Streaming content display with cursor
 */
interface StreamingContentProps {
  content: string;
  isStreaming: boolean;
}

const StreamingContent: React.FC<StreamingContentProps> = ({
  content,
  isStreaming,
}) => {
  if (!content) return null;

  return (
    <div className="prose max-w-none break-words dark:prose-invert">
      <ReactMarkdown>{content}</ReactMarkdown>
      {isStreaming && (
        <span className="inline-block w-2 h-4 bg-orange-500 animate-pulse ml-0.5 align-middle" />
      )}
    </div>
  );
};

/**
 * Error display
 */
interface StreamingErrorProps {
  error: ThreadsStreamingState['error'];
  onRetry?: () => void;
}

const StreamingError: React.FC<StreamingErrorProps> = ({ error, onRetry }) => {
  if (!error) return null;

  return (
    <Alert variant="destructive" className="mt-3">
      <AlertCircle className="h-4 w-4" aria-hidden />
      <AlertDescription className="flex items-center justify-between">
        <span>
          {error.type === 'connection'
            ? 'Connection lost. '
            : error.type === 'parse'
              ? 'Failed to process response. '
              : 'Streaming is not available. '}
          {error.message}
        </span>
        {onRetry && (
          <button
            onClick={onRetry}
            className="text-sm underline hover:no-underline"
          >
            Retry
          </button>
        )}
      </AlertDescription>
    </Alert>
  );
};

/**
 * Main streaming message component
 *
 * Displays the AI response as it streams in, showing:
 * - Planning phase indicator
 * - Tool execution progress with badges
 * - Synthesized content with typing cursor
 * - Error states with retry option
 */
export const StreamingMessage: React.FC<StreamingMessageProps> = ({
  state,
  className = '',
}) => {
  const { phase, content, isStreaming, error, tools } = state;

  // Don't render if idle
  if (phase === 'idle' && !content) {
    return null;
  }

  return (
    <div
      className={`relative mb-6 bg-white dark:bg-gray-800 rounded-lg p-4 ${className}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          {/* Phase indicator (hidden once we have substantial content) */}
          {(phase !== 'complete' || !content) && (
            <div className="mb-3">
              <PhaseIndicator phase={phase} tools={tools} />
            </div>
          )}

          {/* Streaming content */}
          <StreamingContent content={content} isStreaming={isStreaming} />

          {/* Error state */}
          <StreamingError error={error} />
        </div>
      </div>
    </div>
  );
};

export default StreamingMessage;
